import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../common/ModalPopup';
import * as HELPER from '../config/helper';

type AmountValue = number | string | null | undefined;

interface OffsetAgentCommissionPopupProps {
  show: boolean;
  excessAmount: AmountValue;
  agentCommission: AmountValue;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (params: { offsetAmount: number }) => void | Promise<void>;
}

const toAmount = (value: AmountValue) => {
  if (value === null || value === undefined || value === '') return 0;
  const amount = Number(String(value).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(amount) ? amount : 0;
};

const roundAmount = (value: number) => Number(value.toFixed(2));

const formatAmount = (value: AmountValue) => HELPER.addCurrencyFormatting(value);

const formatSignedAmount = (value: AmountValue) => {
  const amount = toAmount(value);
  if (amount < 0) return `- ${formatAmount(Math.abs(amount))}`;
  return formatAmount(amount);
};

const OffsetAgentCommissionPopup: React.FC<OffsetAgentCommissionPopupProps> = ({
  show,
  excessAmount,
  agentCommission,
  loading = false,
  onClose,
  onSubmit,
}) => {
  const [error, setError] = useState('');

  const offsetSummary = useMemo(() => {
    const outstandingAmount = Math.abs(toAmount(excessAmount));
    const currentAgentCommission = toAmount(agentCommission);
    const offsetAmount = roundAmount(Math.min(outstandingAmount, currentAgentCommission));
    const newAgentCommission = roundAmount(currentAgentCommission - offsetAmount);
    const remainingExcessAmountWithOto = roundAmount(toAmount(excessAmount) + offsetAmount);

    return {
      currentAgentCommission,
      offsetAmount,
      newAgentCommission,
      remainingExcessAmountWithOto,
    };
  }, [agentCommission, excessAmount]);

  useEffect(() => {
    if (show) setError('');
  }, [show]);

  const handleSubmit = async () => {
    if (offsetSummary.offsetAmount <= 0) {
      setError('No agent commission amount is available to offset.');
      return;
    }

    if (offsetSummary.offsetAmount > offsetSummary.currentAgentCommission) {
      setError('Offset amount should not exceed existing agent commission.');
      return;
    }

    await onSubmit({
      offsetAmount: offsetSummary.offsetAmount,
    });
  };

  return (
    <div className="offset-agent-commission-popup view-timeline-popup image-timeline-popup">
      <Modal show={show} handleClose={onClose}>
        <div className="modal-header">
          <h2>Offset Agent Commission</h2>
        </div>
        <div className="modal-body">
          <div className="offset-agent-commission-popup__summary">
            <div className="offset-agent-commission-popup__row offset-agent-commission-popup__row--muted">
              <span>Excess Amount with OTO</span>
              <strong className="offset-agent-commission-popup__amount offset-agent-commission-popup__amount--danger">
                {formatSignedAmount(excessAmount)}
              </strong>
            </div>

            <div className="offset-agent-commission-popup__row offset-agent-commission-popup__row--muted">
              <span>Agent Commission</span>
              <strong className="offset-agent-commission-popup__amount">
                {formatAmount(agentCommission)}
              </strong>
            </div>

            <div className="offset-agent-commission-popup__offset-row">
              <span>Offset Agent Commission Amount</span>
              <strong className="offset-agent-commission-popup__offset-amount">
                <span aria-hidden="true">&#10003;</span> {formatAmount(offsetSummary.offsetAmount)}
              </strong>
            </div>

            <div className="offset-agent-commission-popup__row offset-agent-commission-popup__row--success">
              <span>New Agent Commission</span>
              <strong className="offset-agent-commission-popup__amount">
                {formatAmount(offsetSummary.newAgentCommission)}
              </strong>
            </div>

            {/* {offsetSummary.remainingExcessAmountWithOto < 0 && (
              <p className="offset-agent-commission-popup__note">
                <i className="ic-info-icon1"></i>
                <span>
                  Commission fully offset, but Excess still{' '}
                  {formatAmount(Math.abs(offsetSummary.remainingExcessAmountWithOto))}. Remaining outstanding is recovered via{' '}
                  <strong>Add Another Payment</strong>.
                </span>
              </p>
            )} */}

            {error && <small className="error">{error}</small>}
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn-line" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleSubmit}
            disabled={loading || offsetSummary.offsetAmount <= 0}
          >
            Confirm
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default OffsetAgentCommissionPopup;
